/**
 * Toasts — the surface for the two things §6 says must NOT be a dialog.
 *
 *  - §6.10 concurrency is last-write-wins, surfaced as a non-blocking
 *    "Sarah also changed this 2 minutes ago". No conflict dialog, no 409.
 *  - §8 item 4's pattern warning **warns and never blocks** — a
 *    false-positive blocker trains people to route around the system.
 *
 * A deliberately small store rather than a library: both callers are one
 * function call, and a toast that can steal focus would break the §9 keyboard
 * requirement.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  title: string;
  body?: string | undefined;
  tone: ToastTone;
  /** An optional single action — "Reload" on a concurrency notice. */
  action?: { label: string; onClick: () => void } | undefined;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId++;
      setToasts((current) => [...current, { ...toast, id }]);
      // Warnings and errors stay until dismissed: a pattern warning the user
      // never saw is a control that did not happen.
      if (toast.tone === 'info' || toast.tone === 'success') {
        window.setTimeout(() => dismiss(id), 6000);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (value === null) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}

const TONE_STYLES: Record<ToastTone, string> = {
  info: 'border-border bg-card',
  success: 'border-emerald-600/40 bg-emerald-50 text-emerald-950',
  warning: 'border-amber-600/40 bg-amber-50 text-amber-950',
  error: 'border-destructive/40 bg-red-50 text-red-950',
};

/** `role="status"` and `aria-live="polite"`: announced, never focus-stealing (§9). */
function Toaster(): React.ReactElement {
  const { toasts, dismiss } = useToast();

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto rounded-md border p-3 text-sm shadow-lg',
            TONE_STYLES[toast.tone],
          )}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{toast.title}</p>
              {toast.body !== undefined && <p className="mt-0.5 text-xs">{toast.body}</p>}
              {toast.action !== undefined && (
                <button
                  type="button"
                  className="mt-2 text-xs font-medium underline underline-offset-2"
                  onClick={toast.action.onClick}
                >
                  {toast.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              className="rounded p-0.5 hover:bg-black/5"
              onClick={() => dismiss(toast.id)}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
